import React from 'react';
import { Select } from 'antd';
import {DownOutlined} from "@ant-design/icons";
import { t } from '../i18n';
import { useFilterLayoutStyles } from '../utils/languageUtils';

interface OptionType {
    label: string;
    value: string | number | boolean;
    disabled?: boolean;
    secondaryLabel?: string; // 右侧灰色辅助文案（如公司名 / "不可用" 标记），仅在下拉项内显示，不进入选中框
}

interface FilterItemProps {
    label?: string | null;
    options: string[] | OptionType[];
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    spanClassName?: string;
    disabled?: boolean;
    placeholder?: string;
    required?: boolean;
    forceHorizontal?: boolean; // 强制水平布局，如果为 true 则无论语言都使用水平布局
    allowClear?: boolean;
}

/**
 * 单选选择器
 * @description 同delivery/components/ParcelFilterStyle，原组件位于delivery模块，现将组件抽离至components公用模块，便于复用
 *              原引用delivery模块的组件未迁移至公用模块，仍需引用delivery模块的组件
 *              新开发可使用该公用组件
 */
const SingleSelect: React.FC<FilterItemProps> = ({label, options, value, onChange, className, spanClassName, disabled = false, placeholder, required = false, forceHorizontal, allowClear = true}) => {
    // 使用公共 Hook 获取布局样式
    const { containerClassName, labelClassName, inputContainerClassName } = useFilterLayoutStyles(spanClassName, className, forceHorizontal);
    
    // 判断options是简单字符串数组还是对象数组
    const isObjectOptions = options.length > 0 && typeof options[0] !== 'string';

    const handleChange = (selectedValue: string | undefined) => {
        if (onChange) onChange(selectedValue == null ? '' : selectedValue);
    };
    
    return (
        // min-w-0：在 flex/grid 容器中允许收缩，长选项值才能省略号而非撑破布局（非 flex/grid 场景无副作用）
        <div className={`${containerClassName} min-w-0`}>
            {label && (
                <span className={labelClassName}>
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </span>
            )}
            <div className={inputContainerClassName}>
                <Select 
                    value={value || undefined}
                    onChange={handleChange} 
                    className="w-full"
                    placeholder={placeholder || t('components.input.all')}
                    allowClear={allowClear}
                    showSearch
                    suffixIcon={<DownOutlined />}
                    disabled={disabled}
                    size="middle"
                    // 选中框只显示干净的 label（secondaryLabel 仅在下拉项内渲染，不污染选中框）
                    optionLabelProp="label"
                    filterOption={(input, option) => {
                        if (isObjectOptions) {
                            // 对于对象数组，基于 label 进行搜索（显式注解打断 antd 泛型推断的循环引用）
                            const optionLabel: string = String(option?.label ?? '');
                            return optionLabel.toLowerCase().includes(input.toLowerCase());
                        } else {
                            // 对于字符串数组，基于 value 进行搜索
                            return String(option?.value || '').toLowerCase().includes(input.toLowerCase());
                        }
                    }}
                >
                    {isObjectOptions ?
                        // 对象数组选项
                        (options as OptionType[]).map((option) => (
                            <Select.Option key={option.value.toString()} value={option.value.toString()} disabled={option.disabled} label={option.label}>
                                {option.secondaryLabel ? (
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="truncate">{option.label}</span>
                                        <span className="shrink-0 text-gray-400 truncate">{option.secondaryLabel}</span>
                                    </div>
                                ) : option.label}
                            </Select.Option>
                        ))
                        :
                        // 字符串数组选项
                        (options as string[]).map((option) => (
                            <Select.Option key={option} value={option} label={option}>{option}</Select.Option>
                        ))
                    }
                </Select>
            </div>
        </div>
    );
};

export default SingleSelect;
